import { useState, useEffect } from 'react';
import { Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { SearchInput } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { toast } from '@/components/ui/sonner';
import { ToolClientsTable } from '@/modules/tool-clients/components/tool-clients-table';
import {
  useToolClients,
  useToolClientsPagination,
  useToolClientsSearch,
  useCreateToolClient,
} from '@/hooks';
import type { AccessTier } from '@/services';

// ─── Create dialog ────────────────────────────────────────────────────────────

function CreateClientDialog({ onClose }: { onClose: () => void }) {
  const [clientId, setClientId] = useState('');
  const [accessTier, setAccessTier] = useState<AccessTier>('full_access');
  const createClient = useCreateToolClient();

  const handleCreate = async () => {
    if (!clientId.trim()) {
      toast.error('Client ID is required');
      return;
    }
    try {
      await createClient.mutateAsync({ clientId: clientId.trim(), accessTier });
      toast.success(`Client "${clientId}" created`);
      onClose();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create client';
      toast.error(message);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className='max-w-sm'>
        <DialogHeader>
          <DialogTitle>New API Client</DialogTitle>
          <DialogDescription>
            A secret key will be generated automatically. You can view it in the table after creation.
          </DialogDescription>
        </DialogHeader>
        <div className='flex flex-col gap-4'>
          <div className='flex flex-col gap-1.5'>
            <Label htmlFor='clientId'>Client ID</Label>
            <Input
              id='clientId'
              placeholder='e.g. brahma'
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
              autoFocus
            />
            <p className='text-xs text-muted-foreground'>
              Letters, digits, hyphens, and underscores only.
            </p>
          </div>
          <div className='flex flex-col gap-1.5'>
            <Label htmlFor='tier'>Access Tier</Label>
            <Select value={accessTier} onValueChange={(v) => setAccessTier(v as AccessTier)}>
              <SelectTrigger id='tier'>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='full_access'>Full Access</SelectItem>
                <SelectItem value='read_only'>Read Only</SelectItem>
                <SelectItem value='write_only'>Write Only</SelectItem>
                <SelectItem value='dev'>Dev (write tools return dummy data)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant='outline' onClick={onClose}>Cancel</Button>
          <Button onClick={handleCreate} disabled={createClient.isPending}>
            {createClient.isPending ? 'Creating…' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function ToolClientsPage() {
  const { data, isLoading, error, isFetching } = useToolClients();
  const [{ page, limit }, setPagination] = useToolClientsPagination();
  const [search, setSearch] = useToolClientsSearch();
  const [searchInput, setSearchInput] = useState(search);
  const [showCreate, setShowCreate] = useState(false);

  const clients = data?.clients ?? [];
  const totalPages = data?.pagination?.totalPages ?? 1;

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== search) {
        setSearch(searchInput);
        setPagination({ page: 1, limit });
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput, search, setSearch, setPagination, limit]);

  return (
    <div className='flex h-full flex-col gap-6 p-4'>
      <div className='flex items-center justify-between'>
        <div>
          <h1 className='text-base font-semibold'>Tool Server Clients</h1>
          <p className='text-xs text-muted-foreground'>Manage tool-server clients and their access tiers</p>
        </div>
        <Button onClick={() => setShowCreate(true)}>
          <Plus className='mr-2 h-4 w-4' /> New Client
        </Button>
      </div>

      <div className='flex items-center gap-4'>
        <SearchInput
          placeholder='Search by client ID…'
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className='w-[300px] rounded-md'
        />
      </div>

      <div className='flex-1'>
        {error ? (
          <div className='flex h-full items-center justify-center p-6'>
            <div className='max-w-md rounded-lg border border-destructive bg-destructive/10 p-4'>
              <h3 className='font-semibold text-destructive'>Error</h3>
              <p className='mt-1 text-sm text-muted-foreground'>
                {error instanceof Error ? error.message : 'Failed to load clients. Please try again.'}
              </p>
            </div>
          </div>
        ) : (
          <ToolClientsTable
            clients={clients}
            isLoading={isLoading || isFetching}
            page={page}
            limit={limit}
            totalPages={totalPages}
            onPageChange={(newPage) => setPagination({ page: newPage, limit })}
            onPageSizeChange={(newLimit) => setPagination({ page: 1, limit: newLimit })}
          />
        )}
      </div>

      {showCreate && <CreateClientDialog onClose={() => setShowCreate(false)} />}
    </div>
  );
}
